import { lazy, Suspense, type ReactNode } from "react";
import { QueryClientProvider } from "@tanstack/react-query";
import { createBrowserRouter, RouterProvider, type RouteObject } from "react-router";
import { locksSupported, queryClient } from "./api";
import { RequireSession, RoleGate, Shell } from "./components/Shell";
import { NAV, type NavItem } from "./nav";
import { AuditPage } from "./pages/AuditPage";
import { CirclePage } from "./pages/CirclePage";
import { CirclesPage } from "./pages/CirclesPage";
import { EarlyAccessPage } from "./pages/EarlyAccessPage";
import { EnrolPage } from "./pages/EnrolPage";
import { FeaturesPage } from "./pages/FeaturesPage";
import { GrowthPage } from "./pages/GrowthPage";
import { ModerationPage } from "./pages/ModerationPage";
import { NotFoundPage } from "./pages/NotFoundPage";
import { OverviewPage } from "./pages/OverviewPage";
import { PlatformsPage } from "./pages/PlatformsPage";
import { PlaceholderPage } from "./pages/PlaceholderPage";
import { ReportPage } from "./pages/ReportPage";
import { RetentionPage } from "./pages/RetentionPage";
import { SettingsPage } from "./pages/SettingsPage";
import { StaffPage } from "./pages/StaffPage";
import { SystemPage } from "./pages/SystemPage";
import { UserPage } from "./pages/UserPage";
import { UsersPage } from "./pages/UsersPage";

// MapLibre is large: the Map page loads when opened.
const MapPage = lazy(() => import("./pages/MapPage"));

function page(item: NavItem) {
  if (item.path === "/") return <OverviewPage />;
  if (item.path === "/growth") return <GrowthPage />;
  if (item.path === "/retention") return <RetentionPage />;
  if (item.path === "/features") return <FeaturesPage />;
  if (item.path === "/platforms") return <PlatformsPage />;
  if (item.path === "/map")
    return (
      <Suspense fallback={<p className="muted">Loading the map…</p>}>
        <MapPage />
      </Suspense>
    );
  if (item.path === "/staff") return <StaffPage />;
  if (item.path === "/audit") return <AuditPage />;
  if (item.path === "/early-access") return <EarlyAccessPage />;
  if (item.path === "/users") return <UsersPage />;
  if (item.path === "/circles") return <CirclesPage />;
  if (item.path === "/moderation") return <ModerationPage />;
  if (item.path === "/settings") return <SettingsPage />;
  if (item.path === "/system") return <SystemPage />;
  return <PlaceholderPage item={item} />;
}

// Detail pages under a section, behind the same role gate.
const details: Record<string, { path: string; element: ReactNode }> = {
  "/users": { path: ":userId", element: <UserPage /> },
  "/circles": { path: ":circleId", element: <CirclePage /> },
  "/moderation": { path: ":reportId", element: <ReportPage /> },
};

const sections: RouteObject[] = NAV.flatMap((item): RouteObject[] => {
  const element = <RoleGate item={item}>{page(item)}</RoleGate>;
  if (item.path === "/") return [{ index: true, element }];
  const detail = details[item.path];
  return [
    { path: item.path.slice(1), element },
    ...(detail
      ? [{ path: `${item.path.slice(1)}/${detail.path}`, element: <RoleGate item={item}>{detail.element}</RoleGate> }]
      : []),
  ];
});

const router = createBrowserRouter([
  // The emailed link works signed in or out; it signs in as the enrolled staff member.
  { path: "/enrol/:token", element: <EnrolPage /> },
  {
    path: "/",
    element: (
      <RequireSession>
        <Shell />
      </RequireSession>
    ),
    children: [...sections, { path: "*", element: <NotFoundPage /> }],
  },
]);

export function App() {
  if (!locksSupported) {
    return (
      <main className="card-page">
        <section className="card">
          <h1>Browser not supported</h1>
          <p>The admin console needs a current browser (with Web Locks) over HTTPS.</p>
        </section>
      </main>
    );
  }
  return (
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  );
}
